import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type ReactNode } from "react";
import { api } from "../../api/client";
import { qk, useContextSources, useEnvTemplates } from "../../api/queries";
import type { PatchTaskInput, TaskDetail } from "../../api/types";
import { formatUsd } from "../../lib/format";
import { CI_META, ENV_STATE_COLOR, GATE_COLOR, GATE_LABEL, STAGE_KIND_LABEL } from "../../lib/status";
import { useDialogs } from "../Dialogs";
import { useOpenPreview } from "./LivePreview";
import { Icon, type IconName } from "../Icon";
import { Pill, StatusPill } from "../Pill";
import { RelativeTime } from "../RelativeTime";

function Prop({ icon, label, children }: { icon: IconName; label: string; children: ReactNode }) {
  return (
    <div className="prop-row">
      <div className="prop-label">
        <Icon name={icon} size={14} /> {label}
      </div>
      <div className="prop-value">{children}</div>
    </div>
  );
}

export function TaskProperties({ task }: { task: TaskDetail }) {
  const qc = useQueryClient();
  const { confirm } = useDialogs();
  const { data: templates } = useEnvTemplates(task.project_id);
  const { data: sources } = useContextSources(task.project_id);
  const stage = task.stages.find((s) => s.key === task.stage_key);
  const template = templates?.find((t) => t.id === task.env_template_id) ?? templates?.find((t) => t.is_default);
  const templatePorts = Array.isArray(template?.config?.preview_ports)
    ? (template!.config.preview_ports as unknown[]).filter((p): p is number => typeof p === "number")
    : [];
  // The agent's live previews first (each opens at its path), then the template's other ports.
  const declared = (task.previews ?? []).filter((p) => p.env_id === task.environment?.id);
  const previewPorts = templatePorts.filter((p) => !declared.some((d) => d.port === p));

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: qk.task(task.id) });
    qc.invalidateQueries({ queryKey: qk.tasks(task.project_id) });
  };
  const patch = useMutation({
    mutationFn: (p: PatchTaskInput) => api.patchTask(task.id, p),
    onSettled: invalidate,
  });
  const move = useMutation({
    mutationFn: (stageKey: string) => api.moveTask(task.id, stageKey),
    onSettled: invalidate,
  });

  const onStageChange = async (key: string) => {
    const target = task.stages.find((s) => s.key === key);
    if (!target || key === task.stage_key) return;
    const ok = await confirm({
      title: `Move #${task.number} to ${target.name}?`,
      body: <p>This is a manual move and will be recorded in the timeline.</p>,
      confirmLabel: "Move task",
    });
    if (ok) move.mutate(key);
  };

  const env = task.environment;

  return (
    <div className="props">
      <Prop icon="board" label="Stage">
        <select
          className="input input-inline"
          value={task.stage_key}
          onChange={(e) => onStageChange(e.target.value)}
          disabled={move.isPending}
          aria-label="Stage"
        >
          {!stage && <option value={task.stage_key}>{task.stage_key}</option>}
          {task.stages.map((s) => (
            <option key={s.key} value={s.key}>
              {s.name} ({STAGE_KIND_LABEL[s.kind]})
            </option>
          ))}
        </select>
        <span className="muted small">workflow v{task.workflow_version}</span>
      </Prop>
      <Prop icon="dot" label="Status">
        <StatusPill status={task.status} />
        {task.current_gate && (
          <Pill color={GATE_COLOR[task.current_gate.type]}>
            <Icon name="gate" size={12} /> {task.current_gate.name || GATE_LABEL[task.current_gate.type]}
          </Pill>
        )}
        {task.attention_reason && (
          <span className="attention-inline">
            <Icon name="alert" size={13} /> {task.attention_reason}
          </span>
        )}
      </Prop>
      <Prop icon="bot" label="Agent">
        {task.agent_type ? <span>{task.agent_type}</span> : <span className="muted">None</span>}
        {task.current_run && (
          <span className="muted small">
            {task.current_run.kind} run · {task.current_run.status}
            {task.current_run.cost_usd !== null && ` · ${formatUsd(task.current_run.cost_usd)}`}
          </span>
        )}
      </Prop>
      <Prop icon="pr" label="Branch">
        {task.branch ? <code className="code-inline">{task.branch}</code> : <span className="muted">Not created</span>}
      </Prop>
      <Prop icon="merge" label="Pull request">
        {task.pr_number !== null ? (
          <a href={task.pr_url ?? undefined} target="_blank" rel="noopener noreferrer">
            #{task.pr_number} <Icon name="external" size={12} />
          </a>
        ) : (
          <span className="muted">None</span>
        )}
        {task.ci_status && <Pill color={CI_META[task.ci_status].color}>{CI_META[task.ci_status].label}</Pill>}
      </Prop>
      <Prop icon="server" label="Environment">
        <select
          className="input input-inline"
          value={task.env_template_id ?? ""}
          onChange={(e) => patch.mutate({ env_template_id: e.target.value || null })}
          aria-label="Environment template"
          disabled={!!env && env.state !== "terminated"}
          title={env && env.state !== "terminated" ? "The environment already exists" : undefined}
        >
          <option value="">Project default</option>
          {templates?.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
        {env ? (
          <>
            <Pill color={ENV_STATE_COLOR[env.state]}>{env.state}</Pill>
            <span className="muted small" title={env.last_seen_at ?? undefined}>
              {env.connected ? "agent connected" : "agent offline"}
              {env.last_seen_at && (
                <>
                  {" · seen "}
                  <RelativeTime iso={env.last_seen_at} />
                </>
              )}
            </span>
          </>
        ) : (
          <span className="muted small">not provisioned</span>
        )}
      </Prop>
      {(previewPorts.length > 0 || declared.length > 0) && (
        <Prop icon="eye" label="Preview">
          {declared.map((d) => (
            <PreviewButton
              key={d.port}
              taskId={task.id}
              port={d.port}
              path={d.path}
              label={d.label}
              disabled={!env || env.state !== "running" || d.status !== "ready"}
            />
          ))}
          {previewPorts.map((port) => (
            <PreviewButton key={port} taskId={task.id} port={port} disabled={!env || env.state !== "running"} />
          ))}
        </Prop>
      )}
      {env && (env.logs_tail.length > 0 || env.access.ssm_target || env.access.notes) && (
        <Prop icon="terminal" label="Access">
          <details className="env-details">
            <summary>Logs &amp; access</summary>
            {env.access.ssm_target && (
              <div className="small">
                SSM target: <code className="code-inline">{env.access.ssm_target}</code>
              </div>
            )}
            {env.access.notes && <div className="small muted">{env.access.notes}</div>}
            {env.logs_tail.length > 0 && <pre className="logs">{env.logs_tail.join("\n")}</pre>}
          </details>
        </Prop>
      )}
      {sources && sources.length > 0 && (
        <Prop icon="lightbulb" label="Context">
          <div className="checkbox-list">
            {sources.map((s) => {
              const checked = task.context_source_ids.includes(s.id);
              return (
                <label key={s.id} className="checkbox" title={s.description}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() =>
                      patch.mutate({
                        context_source_ids: checked
                          ? task.context_source_ids.filter((x) => x !== s.id)
                          : [...task.context_source_ids, s.id],
                      })
                    }
                  />
                  {s.name}
                </label>
              );
            })}
          </div>
        </Prop>
      )}
      <Prop icon="user" label="Created">
        <span>
          {task.created_by_name} · <RelativeTime iso={task.created_at} />
        </span>
      </Prop>
    </div>
  );
}

function PreviewButton({ taskId, port, path, label, disabled }: { taskId: string; port: number; path?: string; label?: string | null; disabled: boolean }) {
  const { open, busy } = useOpenPreview(taskId);
  return (
    <button
      className="btn btn-sm"
      onClick={() => open(port, path)}
      disabled={disabled || busy}
      title={disabled ? "Environment is not running" : `Open port ${port}${path ?? ""}`}
    >
      <Icon name="external" size={12} /> {label || `:${port}`}
    </button>
  );
}
